import { z } from 'zod';

import type { PatchAdminV1OrgStoresIdBody, Store } from '@nucleus-commerce/api-client/admin';

const TOKEN = '{token}';

/** Blank keeps the backend's default page on the primary domain; otherwise an absolute URL with `{token}`. */
export function isTokenTemplate(value: string): boolean {
  if (value === '') return true;
  if (!value.includes(TOKEN)) return false;
  try {
    const url = new URL(value.replace(TOKEN, 'token'));
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
}

const template = z
  .string()
  .trim()
  .max(500, { error: 'abandonedCheckouts:validation.templateLength' })
  .refine(isTokenTemplate, { error: 'abandonedCheckouts:validation.template' });

export const recoverySettingsSchema = z.object({
  abandonmentMinutes: z
    .string()
    .trim()
    .refine((v) => /^\d+$/.test(v) && Number(v) >= 15 && Number(v) <= 10080, {
      error: 'abandonedCheckouts:validation.minutes',
    }),
  recoveryUrlTemplate: template,
  orderStatusUrlTemplate: template,
});

export type RecoverySettingsForm = z.infer<typeof recoverySettingsSchema>;

export function fromRecoverySettings(store: Pick<Store, 'settings'>): RecoverySettingsForm {
  const { checkout } = store.settings;
  return {
    abandonmentMinutes: String(checkout.abandonmentMinutes),
    recoveryUrlTemplate: checkout.recoveryUrlTemplate ?? '',
    orderStatusUrlTemplate: checkout.orderStatusUrlTemplate ?? '',
  };
}

export function toRecoverySettingsPatch(values: RecoverySettingsForm): PatchAdminV1OrgStoresIdBody {
  return {
    settings: {
      checkout: {
        abandonmentMinutes: Number(values.abandonmentMinutes),
        recoveryUrlTemplate: values.recoveryUrlTemplate || null,
        orderStatusUrlTemplate: values.orderStatusUrlTemplate || null,
      },
    },
  };
}
